<template>
  <div>
    <el-dialog
      :visible="visible"
      :show-close="true"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      custom-class="checklist-modal"
      :append-to-body="true"
      @update:visible="updateVisible"
    >
      <el-form
        ref="checklistForm"
        class="form"
        :model="checklistForm"
        :rules="checklistFormRules"
        @submit.native.prevent
      >
        <h2>Add to checklist</h2>

        <template v-if="checklists && checklists.length > 0">
          <h3>Which checklist do you want to add this to?</h3>
          <el-form-item prop="useExistingChecklist">
            <el-radio-group
              v-model="checklistForm.useExistingChecklist"
              @change="changeChecklistType"
            >
              <el-radio :label="true">Existing checklist</el-radio>
              <el-radio :label="false">Create new checklist</el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item
            v-if="checklistForm.useExistingChecklist"
            prop="selectedChecklist"
          >
            <el-select popper-class="checklist-popper-dropdown" v-model="checklistForm.selectedChecklist">
              <el-option
                v-for="checklist in checklists"
                :key="checklist.id"
                :label="checklist.name"
                :value="checklist.id"
              />
            </el-select>
          </el-form-item>
        </template>

        <el-form-item v-if="shouldCreateNewChecklist" prop="checklistName">
          <el-input
            v-model="checklistForm.checklistName"
            class="checklist-name"
            placeholder="Checklist name"
          />
        </el-form-item>

        <template v-if="topics && topics.length > 0">
          <el-form-item prop="selectedTopics">
            <div class="topics">
              <h3>Which topics do you want to add?</h3>
              <el-checkbox
                :indeterminate="topicsIsIndeterminate"
                class="checkbox-hide-box"
                @change="handleCheckAllChange"
                >{{
                  topics.length === checklistForm.selectedTopics.length
                    ? "Deselect all"
                    : "Select all"
                }}
              </el-checkbox>
              <el-checkbox-group v-model="checklistForm.selectedTopics">
                <el-checkbox
                  v-for="topic in topics"
                  :key="topic.id"
                  :label="topic.label"
                  :value="topic.id"
                ></el-checkbox>
              </el-checkbox-group>
            </div>
          </el-form-item>
        </template>
        <el-button class="submit" @click="submit('checklistForm')"
          >Add to checklist
        </el-button>
      </el-form>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "ChecklistDialog",
  model: {
    prop: "visible",
    event: "update:visible",
  },
  props: {
    visible: Boolean,
    topics: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const consideredEmpty = (value) => {
      if (!value || value.length === 0) {
        return true;
      }
      return value.trim().length === 0;
    };
    const validateChecklistName = (_rule, value, callback) => {
      if (this.shouldCreateNewChecklist && consideredEmpty(value)) {
        callback(new Error("Please provide a checklist name"));
      } else {
        callback();
      }
    };
    const validateSelectedChecklist = (_rule, value, callback) => {
      // In case external checklist id can turn out to be 0...
      if (
        (!this.shouldCreateNewChecklist && value === null) ||
        value === undefined
      ) {
        callback(new Error("Please select an existing checklist"));
      } else {
        callback();
      }
    };
    const requiredTopics = (_rule, value, callback) => {
      if (!this.topics || this.topics.length === 0) {
        callback();
      }

      if (value.length === 0) {
        callback(new Error("Please select at least one topic"));
      } else {
        callback();
      }
    };
    return {
      checklistForm: {
        useExistingChecklist: true,
        selectedChecklist: null,
        checklistName: null,
        selectedTopics: [],
      },
      checklistFormRules: {
        checklistName: [
          {
            validator: validateChecklistName,
            trigger: "blur",
          },
        ],
        selectedChecklist: [
          {
            validator: validateSelectedChecklist,
            trigger: "blur",
          },
        ],
        selectedTopics: [
          {
            validator: requiredTopics,
            trigger: "blur",
          },
        ],
      },
    };
  },
  computed: {
    ...mapState("Workspace", ["checklists"]),
    shouldCreateNewChecklist() {
      // If chosen to create a new one or if there are not existing!
      return (
        !this.checklistForm.useExistingChecklist ||
        (this.checklists && this.checklists.length === 0)
      );
    },
    topicsIsIndeterminate() {
      return (
        this.checklistForm.selectedTopics.length !== 0 &&
        this.checklistForm.selectedTopics.length !== this.topics.length
      );
    },
  },
  methods: {
    updateVisible(value) {
      this.$emit("update:visible", value);
    },
    submit(formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          const submitData = {
            newChecklist: this.shouldCreateNewChecklist,
          };
          if (this.shouldCreateNewChecklist) {
            submitData.checklistName = this.checklistForm.checklistName;
          } else {
            submitData.selectedChecklist = this.checklistForm.selectedChecklist;
          }
          if (this.topics) {
            submitData.selectedTopics = this.checklistForm.selectedTopics.map(
              (selectedLabel) =>
                this.topics.find((element) => element.label === selectedLabel)
                  .id
            );
          }
          this.$emit("submit", submitData);
        }
      });
    },
    handleCheckAllChange() {
      if (
        this.topicsIsIndeterminate ||
        this.checklistForm.selectedTopics.length === 0
      ) {
        this.checklistForm.selectedTopics = this.topics.map(
          (topic) => topic.label
        );
      } else {
        this.checklistForm.selectedTopics = [];
      }
    },
    changeChecklistType(newChecklist) {
      if (newChecklist) {
        this.$refs.checklistForm.clearValidate("selectedChecklist");
      } else {
        this.$refs.checklistForm.clearValidate("checklistName");
      }
    },
  },
};
</script>

<style lang="scss">
@import "../../assets/styles/helpers/mixin";
@import "../../assets/styles/helpers/variables";

.checklist-modal {
  padding: 24px;
  border-radius: 12px;
  max-width: 400px;
  display: flex;
  flex-direction: column;

  .el-form-item {
    margin: 0;
  }

  .el-dialog__header {
    height: 0;
    padding: 0;
    margin: 0;
  }

  .el-dialog__body {
    padding: 0;
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    .el-select {
      width: 100%;
    }

    .form {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      gap: 16px;
    }

    h2 {
      font-weight: 700;
      font-size: 24px;
      line-height: 32px;
      letter-spacing: 0;
      color: #262a2e;
    }

    h3 {
      font-weight: 700;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0;
      color: #262a2e;
    }

    .el-radio-group,
    .el-checkbox-group {
      display: flex;
      flex-direction: column;
      gap: 8px;

      & > * {
        line-height: 24px;
      }

      .el-checkbox__label,
      .el-radio__label {
        color: #262a2e;
        font-weight: 400;
        padding-left: 8px;
      }
    }

    .topics {
      border-top: 1px solid #cfd0d1;
      padding-top: 16px;
      display: flex;
      flex-direction: column;
      gap: 8px;

      h3 {
        margin-bottom: 8px;
      }
    }

    .checkbox-hide-box {
      line-height: 24px;

      .el-checkbox__input {
        display: none;
      }

      .el-checkbox__label {
        color: #0077c1;
        padding: 0;
        line-height: 24px;
        font-size: 16px;
        font-weight: 700;
      }
    }

    .submit {
      background-color: $corporateBlue;
      color: white;
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0;
      border-radius: 8px;
      padding: 10px 16px;
      margin-top: auto;
    }
  }

  .el-select {
    .el-input__inner {
      border-color: black;
      color: black;

      &::placeholder {
        color: black;
      }
    }
    .el-input__suffix .el-select__caret::before {
      color: black;
    }
  }
}
.checklist-popper-dropdown .el-select-dropdown__list .el-select-dropdown__item span {
  color: black;
}
.checklist-name input {
  &::placeholder {
    color: #404040;
  }
  color: black;
  &, &:hover {
    border-color: black;
  }
}
</style>
